// 콜리션 감지 및 처리 매니저
class CollisionManager {
  constructor() {
    this.collisionPairs = []; // 콜리션 쌍들 저장
  }

  // 두 원형 콜라이더 간의 충돌 검사
  checkCircleCollision(pos1, radius1, pos2, radius2) {
    const distance = pos1.distanceTo(pos2);
    return distance < radius1 + radius2;
  }

  // 플레이어와 적 간의 충돌 검사
  checkPlayerEnemyCollision(player, enemy) {
    if (!player || !enemy || !player.isAlive || !enemy.isAliveCheck()) {
      return false;
    }

    const playerPos = player.getPosition();
    const enemyPos = enemy.getPosition();

    return this.checkCircleCollision(
      playerPos,
      player.colliderRadius,
      enemyPos,
      enemy.colliderRadius
    );
  }

  // 적과 적 간의 충돌 검사
  checkEnemyEnemyCollision(enemy1, enemy2) {
    if (
      !enemy1 ||
      !enemy2 ||
      !enemy1.isAliveCheck() ||
      !enemy2.isAliveCheck() ||
      enemy1 === enemy2
    ) {
      return false;
    }

    const pos1 = enemy1.getPosition();
    const pos2 = enemy2.getPosition();

    return this.checkCircleCollision(
      pos1,
      enemy1.colliderRadius,
      pos2,
      enemy2.colliderRadius
    );
  }

  // 플레이어와 모든 적들 간의 충돌 검사
  checkPlayerEnemiesCollisions(player, enemies) {
    const collisions = [];

    for (const enemy of enemies) {
      if (this.checkPlayerEnemyCollision(player, enemy)) {
        collisions.push({
          type: "player-enemy",
          player: player,
          enemy: enemy,
        });
      }
    }

    return collisions;
  }

  // 모든 적들 간의 충돌 검사 (중복 방지)
  checkEnemiesCollisions(enemies) {
    const collisions = [];

    for (let i = 0; i < enemies.length; i++) {
      for (let j = i + 1; j < enemies.length; j++) {
        if (this.checkEnemyEnemyCollision(enemies[i], enemies[j])) {
          collisions.push({
            type: "enemy-enemy",
            enemy1: enemies[i],
            enemy2: enemies[j],
          });
        }
      }
    }

    return collisions;
  }

  // 플레이어와 동료 간의 충돌 검사
  checkPlayerCompanionCollision(player, companion) {
    if (!player || !companion || !player.isAlive || !companion.isAliveCheck()) {
      return false;
    }

    const playerPos = player.getPosition();
    const companionPos = companion.getPosition();

    return this.checkCircleCollision(
      playerPos,
      player.colliderRadius,
      companionPos,
      companion.colliderRadius
    );
  }

  // 동료와 적 간의 충돌 검사
  checkCompanionEnemyCollision(companion, enemy) {
    if (
      !companion ||
      !enemy ||
      !companion.isAliveCheck() ||
      !enemy.isAliveCheck()
    ) {
      return false;
    }

    const companionPos = companion.getPosition();
    const enemyPos = enemy.getPosition();

    return this.checkCircleCollision(
      companionPos,
      companion.colliderRadius,
      enemyPos,
      enemy.colliderRadius
    );
  }

  // 동료와 동료 간의 충돌 검사
  checkCompanionCompanionCollision(companion1, companion2) {
    if (
      !companion1 ||
      !companion2 ||
      !companion1.isAliveCheck() ||
      !companion2.isAliveCheck() ||
      companion1 === companion2
    ) {
      return false;
    }

    const pos1 = companion1.getPosition();
    const pos2 = companion2.getPosition();

    return this.checkCircleCollision(
      pos1,
      companion1.colliderRadius,
      pos2,
      companion2.colliderRadius
    );
  }

  // 플레이어와 모든 동료들 간의 충돌 검사
  checkPlayerCompanionsCollisions(player, companions) {
    const collisions = [];

    for (const companion of companions) {
      if (this.checkPlayerCompanionCollision(player, companion)) {
        collisions.push({
          type: "player-companion",
          player: player,
          companion: companion,
        });
      }
    }

    return collisions;
  }

  // 모든 동료들 간의 충돌 검사
  checkCompanionsCollisions(companions) {
    const collisions = [];

    for (let i = 0; i < companions.length; i++) {
      for (let j = i + 1; j < companions.length; j++) {
        if (
          this.checkCompanionCompanionCollision(companions[i], companions[j])
        ) {
          collisions.push({
            type: "companion-companion",
            companion1: companions[i],
            companion2: companions[j],
          });
        }
      }
    }

    return collisions;
  }

  // 동료와 모든 적들 간의 충돌 검사
  checkCompanionEnemiesCollisions(companion, enemies) {
    const collisions = [];

    for (const enemy of enemies) {
      if (this.checkCompanionEnemyCollision(companion, enemy)) {
        collisions.push({
          type: "companion-enemy",
          companion: companion,
          enemy: enemy,
        });
      }
    }

    return collisions;
  }

  // 모든 콜리션 검사 및 처리
  processAllCollisions(player, enemies, companions = []) {
    const allCollisions = [];

    // 플레이어-적 콜리션 검사
    const playerEnemyCollisions = this.checkPlayerEnemiesCollisions(
      player,
      enemies
    );
    allCollisions.push(...playerEnemyCollisions);

    // 적-적 콜리션 검사
    const enemyEnemyCollisions = this.checkEnemiesCollisions(enemies);
    allCollisions.push(...enemyEnemyCollisions);

    // 플레이어-동료 콜리션 검사
    const playerCompanionCollisions = this.checkPlayerCompanionsCollisions(
      player,
      companions
    );
    allCollisions.push(...playerCompanionCollisions);

    // 동료-동료 콜리션 검사
    const companionCompanionCollisions =
      this.checkCompanionsCollisions(companions);
    allCollisions.push(...companionCompanionCollisions);

    // 동료-적 콜리션 검사
    for (const companion of companions) {
      const companionEnemyCollisions = this.checkCompanionEnemiesCollisions(
        companion,
        enemies
      );
      allCollisions.push(...companionEnemyCollisions);
    }

    return allCollisions;
  }

  // 충돌 해결 - 서로 밀어내기
  resolveCollision(collision) {
    switch (collision.type) {
      case "player-enemy":
        this.resolvePlayerEnemyCollision(collision.player, collision.enemy);
        break;
      case "enemy-enemy":
        this.resolveEnemyEnemyCollision(collision.enemy1, collision.enemy2);
        break;
      case "player-companion":
        this.resolvePlayerCompanionCollision(
          collision.player,
          collision.companion
        );
        break;
      case "companion-companion":
        this.resolveCompanionCompanionCollision(
          collision.companion1,
          collision.companion2
        );
        break;
      case "companion-enemy":
        this.resolveCompanionEnemyCollision(
          collision.companion,
          collision.enemy
        );
        break;
    }
  }

  // 플레이어-적 충돌 해결
  resolvePlayerEnemyCollision(player, enemy) {
    const playerPos = player.getPosition();
    const enemyPos = enemy.getPosition();

    // 충돌 방향 벡터 계산
    const direction = new THREE.Vector3()
      .subVectors(playerPos, enemyPos)
      .normalize();

    // 밀어내기 거리 계산 (반지름의 합보다 약간 더 크게)
    const pushDistance = (player.colliderRadius + enemy.colliderRadius) * 1.1;

    // 각각 반대 방향으로 밀어내기
    const playerNewPos = new THREE.Vector3()
      .copy(enemyPos)
      .add(direction.clone().multiplyScalar(pushDistance));

    const enemyNewPos = new THREE.Vector3()
      .copy(playerPos)
      .add(direction.clone().multiplyScalar(-pushDistance));

    // 플레이어 위치 업데이트 (환경 충돌 검사 포함)
    // if (!player.checkCollision(playerNewPos)) {
    player.model.position.copy(playerNewPos);
    player.position.copy(playerNewPos);
    // }

    // 적 위치 업데이트 (환경 충돌 검사 포함)
    // if (!enemy.checkCollision(enemyNewPos)) {
    enemy.model.position.copy(enemyNewPos);
    enemy.position.copy(enemyNewPos);
    // }
  }

  // 적-적 충돌 해결
  resolveEnemyEnemyCollision(enemy1, enemy2) {
    const pos1 = enemy1.getPosition();
    const pos2 = enemy2.getPosition();

    // 충돌 방향 벡터 계산
    const direction = new THREE.Vector3().subVectors(pos1, pos2).normalize();

    // 밀어내기 거리 계산
    const pushDistance = (enemy1.colliderRadius + enemy2.colliderRadius) * 1.1;

    // 각각 반대 방향으로 밀어내기
    const newPos1 = new THREE.Vector3()
      .copy(pos2)
      .add(direction.clone().multiplyScalar(pushDistance));

    const newPos2 = new THREE.Vector3()
      .copy(pos1)
      .add(direction.clone().multiplyScalar(-pushDistance));

    // 적1 위치 업데이트 (환경 충돌 검사 포함)
    // if (!enemy1.checkCollision(newPos1)) {
    enemy1.model.position.copy(newPos1);
    enemy1.position.copy(newPos1);
    // }

    // 적2 위치 업데이트 (환경 충돌 검사 포함)
    // if (!enemy2.checkCollision(newPos2)) {
    enemy2.model.position.copy(newPos2);
    enemy2.position.copy(newPos2);
    // }
  }

  // 플레이어-동료 충돌 해결
  resolvePlayerCompanionCollision(player, companion) {
    const playerPos = player.getPosition();
    const companionPos = companion.getPosition();

    // 충돌 방향 벡터 계산
    const direction = new THREE.Vector3()
      .subVectors(playerPos, companionPos)
      .normalize();

    // 밀어내기 거리 계산
    const pushDistance =
      (player.colliderRadius + companion.colliderRadius) * 1.1;

    // 각각 반대 방향으로 밀어내기
    const playerNewPos = new THREE.Vector3()
      .copy(companionPos)
      .add(direction.clone().multiplyScalar(pushDistance));

    const companionNewPos = new THREE.Vector3()
      .copy(playerPos)
      .add(direction.clone().multiplyScalar(-pushDistance));

    // 플레이어 위치 업데이트 (환경 충돌 검사 포함)
    // if (!player.checkCollision(playerNewPos)) {
    player.model.position.copy(playerNewPos);
    player.position.copy(playerNewPos);
    // }

    // 동료 위치 업데이트 (환경 충돌 검사 포함)
    // if (!companion.checkCollision(companionNewPos)) {
    companion.position.copy(companionNewPos);
    companion.updatePosition();
    // }
  }

  // 동료-동료 충돌 해결
  resolveCompanionCompanionCollision(companion1, companion2) {
    const pos1 = companion1.getPosition();
    const pos2 = companion2.getPosition();

    // 충돌 방향 벡터 계산
    const direction = new THREE.Vector3().subVectors(pos1, pos2).normalize();

    // 밀어내기 거리 계산
    const pushDistance =
      (companion1.colliderRadius + companion2.colliderRadius) * 1.1;

    // 각각 반대 방향으로 밀어내기
    const newPos1 = new THREE.Vector3()
      .copy(pos2)
      .add(direction.clone().multiplyScalar(pushDistance));

    const newPos2 = new THREE.Vector3()
      .copy(pos1)
      .add(direction.clone().multiplyScalar(-pushDistance));

    // 동료1 위치 업데이트 (환경 충돌 검사 포함)
    // if (!companion1.checkCollision(newPos1)) {
    companion1.position.copy(newPos1);
    companion1.updatePosition();
    // }

    // 동료2 위치 업데이트 (환경 충돌 검사 포함)
    // if (!companion2.checkCollision(newPos2)) {
    companion2.position.copy(newPos2);
    companion2.updatePosition();
    // }
  }

  // 동료-적 충돌 해결
  resolveCompanionEnemyCollision(companion, enemy) {
    const companionPos = companion.getPosition();
    const enemyPos = enemy.getPosition();

    // 충돌 방향 벡터 계산
    const direction = new THREE.Vector3()
      .subVectors(companionPos, enemyPos)
      .normalize();

    // 밀어내기 거리 계산
    const pushDistance =
      (companion.colliderRadius + enemy.colliderRadius) * 1.1;

    // 각각 반대 방향으로 밀어내기
    const companionNewPos = new THREE.Vector3()
      .copy(enemyPos)
      .add(direction.clone().multiplyScalar(pushDistance));

    const enemyNewPos = new THREE.Vector3()
      .copy(companionPos)
      .add(direction.clone().multiplyScalar(-pushDistance));

    // 동료 위치 업데이트 (환경 충돌 검사 포함)
    // if (!companion.checkCollision(companionNewPos)) {
    companion.position.copy(companionNewPos);
    companion.updatePosition();
    // }

    // 적 위치 업데이트 (환경 충돌 검사 포함)
    // if (!enemy.checkCollision(enemyNewPos)) {
    enemy.model.position.copy(enemyNewPos);
    enemy.position.copy(enemyNewPos);
    // }
  }

  // 업데이트 - 매 프레임 호출
  update(player, enemies, companions = []) {
    if (!player) {
      return;
    }

    // enemies가 null이거나 undefined면 빈 배열로 처리
    const validEnemies = enemies || [];

    // 모든 충돌 검사 (적이 없어도 플레이어-동료, 동료-동료 충돌은 검사)
    const collisions = this.processAllCollisions(
      player,
      validEnemies,
      companions
    );

    // TODO : 충돌 검사가 잘 안잡히는 문제가 있음
    // Utils.log(`충돌 검사 결과: ${collisions.length}개`);

    // 충돌 해결
    collisions.forEach((collision) => {
      this.resolveCollision(collision);
    });
  }
}

// webpack 빌드를 위해 default export 추가
export default CollisionManager;
